import { Text } from "@asayinc/component-library";

import { PageContainer } from "src/components/containers/PageContainer/PageContainer";

import { SecondaryTabs, TableV2 as Table } from "@asayinc/component-library";
import { Stack } from "@mui/material";
import {
  FilterList,
  History,
  HistoryOutlined,
  Verified,
  VerifiedOutlined,
  WatchLater,
  WatchLaterOutlined,
  DownloadingOutlined,
  Downloading,
} from "@mui/icons-material";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import { TableMultiSelectFilter } from "src/components/molecules/TableMultiSelectFilter";
import { ROW_OPTIONS } from "src/constants/options/rowOptions";
import { VOTING_PROXY_EVENT_TYPE_OPTIONS } from "src/constants/options/proxyEventOptions";
import { useTablePagination } from "src/hooks/useTablePagination";
import { getColumnData, getColumns, getRowData } from "./utils";
import { TABLE_SEARCH_PARAMS } from "src/constants/tableSearchParams";
import { useTableSearchParams } from "src/hooks/useTableSearchParams";
import { ScrollContainer } from "src/components/containers/ScrollContainer";
import { useNoboEventsQuery } from "src/store/noboEvent";
import { INoboEvent } from "src/store/noboEvent/types";

export type NoboEventsView =
  | "pending_approval"
  | "pending_delivery"
  | "recently_delivered"
  | "historical";

export const NOBO_EVENTS_VIEW: { [key: string]: NoboEventsView } = {
  PENDING_APPROVAL: "pending_approval",
  PENDING_DELIVERY: "pending_delivery",
  RECENTLY_DELIVERED: "recently_delivered",
  HISTORICAL: "historical",
};

export function NoboEvents() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const methods = useForm();
  const view = (searchParams.get(TABLE_SEARCH_PARAMS.VIEW) ||
    NOBO_EVENTS_VIEW.PENDING_APPROVAL) as NoboEventsView;
  const { type } = useTableSearchParams();

  const {
    getSort,
    offset,
    limit,
    ordering,
    onChangePage,
    onChangeRows,
    onOrderingChange,
  } = useTablePagination();
  const { data, isFetching, isLoading } = useNoboEventsQuery({
    offset,
    limit,
    ordering,
    type,
    tab: view || NOBO_EVENTS_VIEW.PENDING_APPROVAL,
  });

  const paginateProps = {
    onChangePage,
    onChangeRows,
    rowOptions: ROW_OPTIONS,
    count: data?.count || 0,
    page: Math.floor(offset / limit) + 1,
    rowsPerPage: limit,
  };

  const handleRowClick = (event: INoboEvent) => {
    const { id } = event;
    navigate(`${id}/details`);
  };

  const handleSecondaryTabChange = (tab: string) => {
    const newParams = new URLSearchParams({
      view: tab,
    });
    setSearchParams(newParams);
  };

  const columns = useMemo(() => getColumns(view), [view]);
  const columnData = useMemo(
    () => getColumnData({ getSort, onOrderingChange }),
    [getSort, onOrderingChange]
  );
  const rowData = getRowData(data?.results || [], handleRowClick);

  const filterList = (
    <FormProvider {...methods}>
      <TableMultiSelectFilter
        fieldName={TABLE_SEARCH_PARAMS.TYPE}
        label="Types"
        options={VOTING_PROXY_EVENT_TYPE_OPTIONS}
        queryParameter={TABLE_SEARCH_PARAMS.TYPE}
        view={view}
      />
    </FormProvider>
  );

  return (
    <PageContainer>
      <Text variant="h4" sx={{ pb: 4, pt: 10, px: 10 }}>
        Nobo Events
      </Text>
      <ScrollContainer sx={{ pt: 4, pb: 10, pl: 10, pr: 6.25 }}>
        <Stack>
          <SecondaryTabs
            sx={{ mb: 8, mr: "auto" }}
            tabs={[
              {
                label: "Pending approval",
                value: NOBO_EVENTS_VIEW.PENDING_APPROVAL,
                icon:
                  view === NOBO_EVENTS_VIEW.PENDING_APPROVAL ? (
                    <WatchLater style={{ color: "#1968FF" }} />
                  ) : (
                    <WatchLaterOutlined />
                  ),
              },
              {
                label: "Pending delivery",
                value: NOBO_EVENTS_VIEW.PENDING_DELIVERY,
                icon:
                  view === NOBO_EVENTS_VIEW.PENDING_DELIVERY ? (
                    <Downloading style={{ color: "#1968FF" }} />
                  ) : (
                    <DownloadingOutlined />
                  ),
              },
              {
                label: "Recently delivered",
                value: NOBO_EVENTS_VIEW.RECENTLY_DELIVERED,
                icon:
                  view === NOBO_EVENTS_VIEW.RECENTLY_DELIVERED ? (
                    <Verified style={{ color: "#1968FF" }} />
                  ) : (
                    <VerifiedOutlined />
                  ),
              },
              {
                label: "Historical",
                value: NOBO_EVENTS_VIEW.HISTORICAL,
                icon:
                  view === NOBO_EVENTS_VIEW.HISTORICAL ? (
                    <History style={{ color: "#1968FF" }} />
                  ) : (
                    <HistoryOutlined />
                  ),
              },
            ]}
            activeTab={view}
            clickHandler={handleSecondaryTabChange}
          />
        </Stack>
        <Table
          count={data?.count || 0}
          columnData={columnData}
          columns={columns}
          isFetching={isFetching}
          isLoading={isLoading}
          noResultsData={{
            title: "No Nobo Events found",
            subtitle:
              "Sorry, there are no Nobo Events that match your applied filters.",
          }}
          paginateProps={paginateProps}
          rows={rowData}
          tableMinWidth={1100}
          title={
            view === NOBO_EVENTS_VIEW.PENDING_APPROVAL
              ? "Pending Approval Nobo Events"
              : view === NOBO_EVENTS_VIEW.PENDING_DELIVERY
              ? "Pending Delivery Nobo Events"
              : view === NOBO_EVENTS_VIEW.RECENTLY_DELIVERED
              ? "Recently Delivered Nobo Events"
              : "Historical Nobo Events"
          }
          titleVariant="subtitle2"
          toolbarInside
          toolbarCollapse={{
            Icon: FilterList,
            collapseContent: filterList,
            isFlat: true,
          }}
        />
      </ScrollContainer>
    </PageContainer>
  );
}
